import { useState, useEffect } from "react";
import { type LifecycleState, STATE_LABELS, STATE_COLORS } from "../plm/lifecycle";

interface ShareDocInfo {
  id: string;
  name: string;
  part_number: string;
  revision: string;
  state: LifecycleState;
  doc_type: string;
  thumbnail_url?: string;
  owner_name?: string;
  created_at: string;
  updated_at: string;
}

export default function ShareViewer({ token }: { token: string }) {
  const [doc, setDoc] = useState<ShareDocInfo | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`/api/s/${token}`, { signal: controller.signal })
      .then(async (res) => {
        if (!res.ok) throw new Error("Link expired or not found");
        return res.json() as Promise<ShareDocInfo>;
      })
      .then(setDoc)
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : "Failed to load document");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [token]);

  if (loading) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.card}>
          <div style={styles.loading}>Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#c44" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          </div>
          <h1 style={styles.errorTitle}>Link Expired or Not Found</h1>
          <p style={styles.errorText}>This share link may have expired or been revoked.</p>
          <a href="/" style={styles.openBtn}>Go to CAD-PLM</a>
        </div>
      </div>
    );
  }

  const colors = STATE_COLORS[doc.state] || STATE_COLORS.wip;
  const stateLabel = STATE_LABELS[doc.state] || doc.state;

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.logo}>
            <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
              <path d="M2 9L6 2L10 9H2Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
          </div>
          <span style={styles.brand}>CAD-PLM</span>
          <span style={{ ...styles.stateBadge, background: colors.bg, color: colors.fg }}>
            {stateLabel}
          </span>
        </div>

        {doc.thumbnail_url && (
          <div style={styles.thumbnailWrap}>
            <img
              src={doc.thumbnail_url}
              alt="Document preview"
              style={styles.thumbnail}
              crossOrigin="anonymous"
              onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = "none"; }}
            />
          </div>
        )}

        <h1 style={styles.docName}>{doc.name}</h1>

        <div style={styles.meta}>
          {doc.part_number && (
            <div style={styles.metaRow}>
              <span style={styles.metaLabel}>Part Number</span>
              <span style={styles.metaValue}>{doc.part_number}</span>
            </div>
          )}
          {doc.revision && (
            <div style={styles.metaRow}>
              <span style={styles.metaLabel}>Revision</span>
              <span style={styles.metaValue}>{doc.revision}</span>
            </div>
          )}
          <div style={styles.metaRow}>
            <span style={styles.metaLabel}>Type</span>
            <span style={styles.metaValue}>{doc.doc_type}</span>
          </div>
          {doc.owner_name && (
            <div style={styles.metaRow}>
              <span style={styles.metaLabel}>Owner</span>
              <span style={styles.metaValue}>{doc.owner_name}</span>
            </div>
          )}
          <div style={styles.metaRow}>
            <span style={styles.metaLabel}>Updated</span>
            <span style={styles.metaValue}>{new Date(doc.updated_at).toLocaleDateString()}</span>
          </div>
        </div>

        <a href={`/documents/${doc.id}`} style={styles.openBtn}>
          Open in CAD-PLM
        </a>
        <p style={styles.hint}>Sign in required to view the full document</p>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#0d1117",
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    padding: 24,
  },
  card: {
    background: "#161b22",
    border: "1px solid #30363d",
    borderRadius: 12,
    padding: 32,
    maxWidth: 420,
    width: "100%",
    textAlign: "center" as const,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginBottom: 24,
  },
  logo: {
    width: 28,
    height: 28,
    borderRadius: 6,
    background: "linear-gradient(135deg, #58a6ff 0%, #3060c0 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  brand: {
    fontSize: 14,
    fontWeight: 700,
    color: "#e6edf3",
    letterSpacing: "0.04em",
  },
  stateBadge: {
    fontSize: 10,
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: 12,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    marginLeft: 8,
  },
  thumbnailWrap: {
    background: "#0d1117",
    borderRadius: 8,
    padding: 8,
    marginBottom: 20,
    border: "1px solid #21262d",
  },
  thumbnail: {
    width: "100%",
    height: "auto",
    borderRadius: 4,
    display: "block",
  },
  docName: {
    fontSize: 22,
    fontWeight: 600,
    color: "#e6edf3",
    margin: "0 0 16px",
  },
  meta: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 8,
    marginBottom: 24,
    textAlign: "left" as const,
  },
  metaRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
    borderBottom: "1px solid #21262d",
  },
  metaLabel: {
    fontSize: 12,
    color: "#8b949e",
    fontWeight: 500,
  },
  metaValue: {
    fontSize: 12,
    color: "#e6edf3",
    fontWeight: 500,
  },
  openBtn: {
    display: "inline-block",
    background: "#58a6ff",
    color: "#fff",
    textDecoration: "none",
    padding: "10px 24px",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    border: "none",
  },
  hint: {
    fontSize: 11,
    color: "#8b949e",
    marginTop: 12,
    marginBottom: 0,
  },
  loading: {
    color: "#8b949e",
    padding: 40,
  },
  errorIcon: {
    marginBottom: 16,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: "#e6edf3",
    margin: "0 0 8px",
  },
  errorText: {
    fontSize: 13,
    color: "#8b949e",
    margin: "0 0 20px",
  },
};
